import { useState } from "react";
import { Link } from "react-router-dom";
import { Volume2, Trash2, Check, Search } from "lucide-react";
import { getVocab, removeWord, toggleLearned } from "@/lib/vocab";
import { speak, cancelSpeech } from "@/lib/speech";
import { useVocabTick } from "@/components/SaveWords";

const play = (t, rate) => { cancelSpeech(); speak(t, { rate }); };
const FILTERS = [["all", "All"], ["learning", "Learning"], ["learned", "Learned"]];

export default function Vocabulary() {
  useVocabTick();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const all = getVocab();
  const list = all.filter((x) => x.word.includes(q.toLowerCase().trim()) && (filter === "all" || (filter === "learned") === x.learned));

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-8">
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-indigo-300">Personal list</span>
      <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Vocabulary Notebook</h1>
      <p className="mt-2 text-slate-400">Words you missed in practice and exams. Listen to each one slowly, hear it in context, and mark it learned once it sticks.</p>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="vocab-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search words…" className="w-64 rounded-full border border-slate-700 bg-slate-800 py-2 pl-9 pr-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" /></div>
        <div className="flex rounded-full bg-slate-800 p-1">
          {FILTERS.map(([v, l]) => <button key={v} data-testid={`vocab-filter-${v}`} onClick={() => setFilter(v)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === v ? "bg-indigo-600 text-white" : "text-slate-300 hover:text-white"}`}>{l}</button>)}
        </div>
        <span data-testid="vocab-count" className="ml-auto font-mono text-xs text-slate-400">{all.filter((x) => x.learned).length}/{all.length} learned</span>
      </div>

      {all.length === 0 ? (
        <div data-testid="vocab-empty" className="mt-6 rounded-xl bg-white p-10 text-center text-slate-500">Your notebook is empty. After answering in the <Link to="/practice" className="font-semibold text-indigo-600 hover:underline">Practice Arena</Link>, tap a missed word to save it here.</div>
      ) : (
        <div className="mt-6 grid gap-3 md:grid-cols-2" data-testid="vocab-list">
          {list.map((x) => (
            <div key={x.word} data-testid={`vocab-item-${x.word}`} className={`rounded-xl bg-white p-5 shadow-xl shadow-slate-950/30 transition-opacity ${x.learned ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className={`font-display text-2xl font-bold text-slate-900 ${x.learned ? "line-through decoration-emerald-500" : ""}`}>{x.word}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{x.part && `Part ${x.part} · `}{new Date(x.addedAt).toLocaleDateString()}</div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`vocab-play-${x.word}`} onClick={() => play(x.word, 0.7)} className="rounded-full bg-indigo-600 p-2 text-white hover:bg-indigo-700" aria-label="Play word"><Volume2 className="h-4 w-4" /></button>
                  <button data-testid={`vocab-learned-${x.word}`} onClick={() => toggleLearned(x.word)} className={`rounded-full p-2 ${x.learned ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-emerald-100"}`} aria-label="Mark learned"><Check className="h-4 w-4" /></button>
                  <button data-testid={`vocab-delete-${x.word}`} onClick={() => removeWord(x.word)} className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-rose-100 hover:text-rose-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {x.context && (
                <button data-testid={`vocab-context-${x.word}`} onClick={() => play(x.context, 0.9)} className="mt-3 flex w-full items-start gap-2 rounded-lg bg-slate-50 p-3 text-left text-sm text-slate-600 hover:bg-slate-100">
                  <Volume2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span>{x.context.split(new RegExp(`(\\b${x.word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b)`, "i")).map((s, i) => (i % 2 ? <mark key={i} className="rounded bg-amber-200 px-0.5 text-slate-900">{s}</mark> : s))}</span>
                </button>
              )}
            </div>
          ))}
          {list.length === 0 && <p data-testid="vocab-no-match" className="text-sm text-slate-400">No words match this filter.</p>}
        </div>
      )}
    </div>
  );
}
